import { Link } from "react-router-dom";
import { useCompany } from "@/lib/company";
import { useLang } from "@/lib/i18n";

export default function CGV() {
  const { company } = useCompany();
  const { t } = useLang();

  const lastUpdate = "01/02/2026";
  const email = company?.email || "contact@sukanacoop.com";
  const phone = company?.phone || "";
  const name = company?.name || "GNL SERVICES ET CONSULTING SARL";

  const Section = ({ title, children, testid }) => (
    <section className="mt-10" data-testid={testid}>
      <h2 className="heading font-bold text-xl text-[#111812]">{title}</h2>
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-[#1F2A22]">
        {children}
      </div>
    </section>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="cgv-page">
      <span className="label-uppercase text-xs text-[#C85A32]">{t("cgv.category")}</span>
      <h1 className="heading font-extrabold text-4xl lg:text-5xl mt-2 text-[#111812]" data-testid="cgv-title">
        {t("cgv.title")}
      </h1>
      <p className="mt-3 text-sm text-[#4B5563]">
        {t("cgv.last_update")} : <strong>{lastUpdate}</strong>
      </p>

      <div className="mt-6 p-4 bg-[#F5F5F0] rounded-lg text-sm">
        <p>
          <strong>SUKANACOOP</strong> {t("cgv.intro_pre")} <strong>{name}</strong>. {t("cgv.intro_post")}
        </p>
      </div>

      <Section title={t("cgv.s1_title")} testid="cgv-s1">
        <p>{t("cgv.s1_p1")}</p>
        <p>{t("cgv.s1_p2")}</p>
      </Section>

      <Section title={t("cgv.s2_title")} testid="cgv-s2">
        <p>{t("cgv.s2_p1")}</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>{t("cgv.s2_li1")}</li>
          <li>{t("cgv.s2_li2")}</li>
          <li>{t("cgv.s2_li3")}</li>
        </ul>
      </Section>

      <Section title={t("cgv.s3_title")} testid="cgv-s3">
        <p>{t("cgv.s3_p1")}</p>
        <p>{t("cgv.s3_p2")}</p>
      </Section>

      <Section title={t("cgv.s4_title")} testid="cgv-s4">
        <p>{t("cgv.s4_p1")}</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>PawaPay Mobile Money</strong> — Vodacom M-Pesa, Airtel Money, Orange Money (RDC).</li>
          <li><strong>{t("cgv.s4_transfer")}</strong> — {t("cgv.s4_transfer_desc")}</li>
        </ul>
        <p>{t("cgv.s4_p2")}</p>
      </Section>

      <Section title={t("cgv.s5_title")} testid="cgv-s5">
        <p>{t("cgv.s5_p1")}</p>
        <p>{t("cgv.s5_p2")}</p>
      </Section>

      <Section title={t("cgv.s6_title")} testid="cgv-s6-refund">
        <div className="p-4 bg-[#FFF7ED] border-l-4 border-[#D98A2C] rounded">
          <p className="font-semibold text-[#111812]">{t("cgv.s6_summary_title")}</p>
          <ul className="list-disc pl-6 mt-2 space-y-1 text-sm">
            <li>{t("cgv.s6_summary_li1")}</li>
            <li>{t("cgv.s6_summary_li2")}</li>
            <li>{t("cgv.s6_summary_li3")}</li>
          </ul>
        </div>

        <h3 className="heading font-semibold text-lg mt-6">{t("cgv.s6_h1")}</h3>
        <p>{t("cgv.s6_p1")}</p>

        <h3 className="heading font-semibold text-lg mt-6">{t("cgv.s6_h2")}</h3>

        <div className="mt-3 p-4 rounded-lg border border-[#1A3322]/15 bg-[#F5F5F0]" data-testid="cgv-inapp-refund">
          <p className="font-bold text-[#1A3322]">✨ {t("cgv.s6_inapp_title")}</p>
          <p className="mt-2">
            {t("cgv.s6_inapp_desc")}{" "}
            <Link to="/commandes" className="text-[#C85A32] font-semibold underline">
              {t("cgv.s6_inapp_link")}
            </Link>
            {t("cgv.s6_inapp_desc_after")}
          </p>
          <ol className="list-decimal pl-6 mt-2 space-y-1 text-sm">
            <li>{t("cgv.s6_inapp_step1")}</li>
            <li>{t("cgv.s6_inapp_step2")}</li>
            <li>{t("cgv.s6_inapp_step3")}</li>
          </ol>
        </div>

        <p className="font-semibold mt-4">{t("cgv.s6_email_fallback_title")}</p>
        <p>{t("cgv.s6_p2")}</p>
        <ol className="list-decimal pl-6 space-y-1">
          <li>
            {t("cgv.s6_step1_pre")}{" "}
            <a href={`mailto:${email}?subject=Demande%20de%20remboursement`} className="text-[#1A3322] underline">
              {email}
            </a>
            {" "}{t("cgv.s6_step1_post")}
          </li>
          <li>{t("cgv.s6_step2")}</li>
          <li>{t("cgv.s6_step3")}</li>
          <li>{t("cgv.s6_step4")}</li>
        </ol>

        <p className="mt-3 p-3 rounded-md bg-[#FFF7ED] border border-[#C85A32]/25 text-sm" data-testid="cgv-return-note">
          {t("cgv.s6_return_note")}
        </p>

        <h3 className="heading font-semibold text-lg mt-6">{t("cgv.s6_h3")}</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>{t("cgv.s6_ch_pawapay")}</li>
          <li>{t("cgv.s6_ch_transfer")}</li>
        </ul>

        <h3 className="heading font-semibold text-lg mt-6">{t("cgv.s6_h4")}</h3>
        <p>{t("cgv.s6_p4")}</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>{t("cgv.s6_excl1")}</li>
          <li>{t("cgv.s6_excl2")}</li>
          <li>{t("cgv.s6_excl3")}</li>
        </ul>
      </Section>

      <Section title={t("cgv.s7_title")} testid="cgv-s7">
        <p>{t("cgv.s7_p1")}</p>
      </Section>

      <Section title={t("cgv.s8_title")} testid="cgv-s8">
        <p>{t("cgv.s8_p1")}</p>
      </Section>

      <Section title={t("cgv.s9_title")} testid="cgv-s9">
        <p>{t("cgv.s9_p1")}</p>
      </Section>

      <Section title={t("cgv.s10_title")} testid="cgv-s10-contact">
        <p>{t("cgv.s10_p1")}</p>
        <ul className="space-y-1">
          <li>
            <span className="text-[#4B5563] inline-block w-24">Email :</span>
            <a href={`mailto:${email}`} className="text-[#1A3322] underline">{email}</a>
          </li>
          {phone && (
            <li>
              <span className="text-[#4B5563] inline-block w-24">{t("cgv.phone_label")} :</span>
              <a href={`tel:${phone}`} className="text-[#1A3322] underline">{phone}</a>
            </li>
          )}
          <li>
            <span className="text-[#4B5563] inline-block w-24">Chat :</span>
            <span>{t("cgv.chat_hint")}</span>
          </li>
        </ul>
      </Section>

      <div className="mt-12 text-xs text-[#4B5563] border-t pt-6">
        {t("cgv.footer_note_pre")} <strong>{name}</strong>. {t("cgv.footer_note_post")}
      </div>
    </div>
  );
}
